import { useState } from 'react';

export default function Hero() {
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen bg-[#1A1C1E] overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(#00E5FF 1px, transparent 1px), linear-gradient(90deg, #00E5FF 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Animated workflow lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00E5FF]/30 to-transparent animate-pulse"></div>
        <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00E5FF]/20 to-transparent animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00E5FF]/30 to-transparent animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-40 pb-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            <div className="space-y-5">
              {/* Tagline badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse"></div>
                <span className="text-[#00E5FF] text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Intelligent Architecture. Effortless Flow.
                </span>
              </div>

              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-[#F4F7F6] leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Intelligent Architecture for Modern Businesses
              </h1>

              <p className="text-lg text-[#00E5FF]/80 font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                Building the digital backbone of your business.
              </p>

              <h2 className="text-xl lg:text-2xl text-[#7F8C8D] font-medium leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Graystone AI designs automated systems that remove operational friction and restore clarity to growing organizations.
              </h2>

              <p className="text-base text-[#7F8C8D] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                We identify the tasks your business repeats every day — reporting, follow-ups, data entry, scheduling, and operational handoffs — and build intelligent systems that handle them automatically.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onMouseEnter={() => setHoveredButton('primary')}
                onMouseLeave={() => setHoveredButton(null)}
                onClick={() => handleScroll('#how-it-works')}
                className="relative px-8 py-4 bg-[#00E5FF] text-[#1A1C1E] font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: hoveredButton === 'primary' ? '0 0 30px rgba(0, 229, 255, 0.6)' : '0 0 15px rgba(0, 229, 255, 0.3)',
                  transform: hoveredButton === 'primary' ? 'translateY(-2px)' : 'translateY(0)'
                }}
              >
                See How It Works
              </button>

              <button
                onMouseEnter={() => setHoveredButton('secondary')}
                onMouseLeave={() => setHoveredButton(null)}
                onClick={() => handleScroll('#contact')}
                className="relative px-8 py-4 bg-transparent border-2 border-[#00E5FF] text-[#00E5FF] font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: hoveredButton === 'secondary' ? '0 0 30px rgba(0, 229, 255, 0.4)' : 'none',
                  transform: hoveredButton === 'secondary' ? 'translateY(-2px)' : 'translateY(0)'
                }}
              >
                Request a Systems Consultation
              </button>
            </div>
          </div>

          {/* Right Column - Isometric Cube Visual */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square">
              {/* Glowing circuit lines background */}
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-full h-full opacity-30" viewBox="0 0 400 400">
                  <defs>
                    <linearGradient id="circuitGlow" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#00E5FF', stopOpacity: 0.8 }} />
                      <stop offset="100%" style={{ stopColor: '#00E5FF', stopOpacity: 0.2 }} />
                    </linearGradient>
                  </defs>
                  <path d="M50 200 L150 200 L150 100 L250 100" stroke="url(#circuitGlow)" strokeWidth="2" fill="none" className="animate-pulse" />
                  <path d="M350 200 L250 200 L250 300 L150 300" stroke="url(#circuitGlow)" strokeWidth="2" fill="none" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
                  <path d="M200 50 L200 150 L300 150 L300 250" stroke="url(#circuitGlow)" strokeWidth="2" fill="none" className="animate-pulse" style={{ animationDelay: '1s' }} />
                  <path d="M200 350 L200 250 L100 250 L100 150" stroke="url(#circuitGlow)" strokeWidth="2" fill="none" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
                  <circle cx="150" cy="100" r="4" fill="#00E5FF" className="animate-pulse" />
                  <circle cx="250" cy="300" r="4" fill="#00E5FF" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
                  <circle cx="300" cy="150" r="4" fill="#00E5FF" className="animate-pulse" style={{ animationDelay: '1s' }} />
                  <circle cx="100" cy="250" r="4" fill="#00E5FF" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
                </svg>
              </div>

              {/* Isometric Cube with G */}
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/f95a8b76717595cf01b4b6f3a86af516.png"
                  alt="Graystone AI Logo"
                  className="w-64 h-64 object-contain drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 0 40px rgba(0, 229, 255, 0.5))' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Credibility Indicators */}
        <div className="mt-24 pt-12 border-t border-[#7F8C8D]/20">
          <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">
            {[
              'Built for businesses between $1M\u2013$10M revenue',
              'Designed by automation architects',
              'Supporting finance, healthcare, legal, and real estate firms'
            ].map((text) => (
              <div key={text} className="flex items-start justify-center md:justify-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2 flex-shrink-0"></div>
                <p className="text-[#7F8C8D] text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
